import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { db } from '../lib/firebase';
import type { Project, ProjectRole, UserLookup } from '../lib/types';

/**
 * Projects live at the top level, not under an owner's uid.
 * They have to: a project shared with a colleague must be reachable from their
 * account, and nothing under users/{uid} ever can be.
 */
export const projectsCol = () => collection(db, 'projects');
export const projectDoc = (projectId: string) => doc(db, 'projects', projectId);

/**
 * Every project the signed-in user can see, owned or shared.
 * One array-contains query covers both; the caller splits them by ownerUid.
 */
export function watchProjects(
  uid: string,
  cb: (p: Project[]) => void,
  onError?: (e: Error) => void,
) {
  const q = query(
    projectsCol(),
    where('memberUids', 'array-contains', uid),
    orderBy('updatedAt', 'desc'),
  );
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Project)),
    (e) => onError?.(e),
  );
}

export function watchProject(
  projectId: string,
  cb: (p: Project | null) => void,
  onError?: (e: Error) => void,
) {
  return onSnapshot(
    projectDoc(projectId),
    (snap) => cb(snap.exists() ? ({ id: snap.id, ...snap.data() } as Project) : null),
    (e) => onError?.(e),
  );
}

export async function createProject(user: User, name: string, description = '') {
  // Every project lives under the creator's organisation. Read from the
  // account (written by the server), never from this device's memory.
  const { getDoc } = await import('firebase/firestore');
  const account = await getDoc(doc(db, 'users', user.uid)).catch(() => null);
  const labCode = (account?.data()?.labCode as string | undefined) ?? null;
  const ref = await addDoc(projectsCol(), {
    labCode,
    name: name.trim(),
    description: description.trim(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    formulationCount: 0,
    // Starts empty on purpose: the owner chooses what this project records.
    readers: [],
    ownerUid: user.uid,
    memberUids: [user.uid],
    members: {
      [user.uid]: {
        role: 'owner',
        email: user.email,
        displayName: user.displayName,
        addedAt: Timestamp.now(),
      },
    },
  });
  return ref.id;
}

export async function updateProject(
  projectId: string,
  patch: Partial<Pick<Project, 'name' | 'description'>>,
) {
  await updateDoc(projectDoc(projectId), { ...patch, updatedAt: serverTimestamp() });
}

export async function touchProject(projectId: string, formulationDelta = 0) {
  const patch: Record<string, ReturnType<typeof serverTimestamp> | ReturnType<typeof increment>> = {
    updatedAt: serverTimestamp(),
  };
  if (formulationDelta !== 0) patch.formulationCount = increment(formulationDelta);
  await updateDoc(projectDoc(projectId), patch);
}

/** Firestore does not cascade — subcollections would survive and stay billable. */
export async function deleteProject(projectId: string) {
  // Readings too: left behind, they would outlive the project that owned them.
  for (const sub of ['formulations', 'captures', 'readings']) {
    const snap = await getDocs(collection(db, 'projects', projectId, sub));
    for (let i = 0; i < snap.docs.length; i += 400) {
      const batch = writeBatch(db);
      snap.docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
  }
  await deleteDoc(projectDoc(projectId));
}

// ---------------------------------------------------------------------------
// Sharing
// ---------------------------------------------------------------------------

const lookupKey = (email: string) => email.trim().toLowerCase();

/**
 * Published on sign-in so colleagues can be found by address.
 * Rules permit `get` on a known key and deny `list`, so this cannot be scraped
 * for a list of everyone who uses the app.
 */
export async function publishUserLookup(user: User) {
  if (!user.email) return;
  await updateDoc(doc(db, 'userLookup', lookupKey(user.email)), {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
  }).catch(async () => {
    const { setDoc } = await import('firebase/firestore');
    await setDoc(doc(db, 'userLookup', lookupKey(user.email!)), {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
    });
  });
}

export type ShareResult =
  | { ok: true }
  | { ok: false; reason: 'no-account' | 'already-member' | 'self' | 'denied' };

export async function shareProject(
  project: Project,
  email: string,
  role: Exclude<ProjectRole, 'owner'>,
  actingUid: string,
): Promise<ShareResult> {
  const key = lookupKey(email);
  if (!key) return { ok: false, reason: 'no-account' };

  const snap = await getDoc(doc(db, 'userLookup', key));
  if (!snap.exists()) return { ok: false, reason: 'no-account' };

  const target = snap.data() as UserLookup;
  if (target.uid === actingUid) return { ok: false, reason: 'self' };
  if (project.memberUids?.includes(target.uid)) return { ok: false, reason: 'already-member' };

  try {
    await updateDoc(projectDoc(project.id), {
      memberUids: arrayUnion(target.uid),
      [`members.${target.uid}`]: {
        role,
        email: target.email,
        displayName: target.displayName ?? null,
        addedAt: Timestamp.now(),
      },
      updatedAt: serverTimestamp(),
    });
    return { ok: true };
  } catch {
    return { ok: false, reason: 'denied' };
  }
}

/**
 * Share with an account we already know the uid of.
 *
 * The email path has to go via `userLookup`, which only exists once someone has
 * signed in and published it — hence the "they must have opened the app once"
 * caveat in the dialog. A lab mate picked from the dropdown is already a real
 * account with a real uid, so none of that applies and the write is direct.
 */
export async function shareProjectWithMember(
  project: Project,
  member: { uid: string; email: string; displayName?: string | null },
  role: Exclude<ProjectRole, 'owner'>,
  actingUid: string,
): Promise<ShareResult> {
  if (member.uid === actingUid) return { ok: false, reason: 'self' };
  if (project.memberUids?.includes(member.uid)) return { ok: false, reason: 'already-member' };

  try {
    await updateDoc(projectDoc(project.id), {
      memberUids: arrayUnion(member.uid),
      [`members.${member.uid}`]: {
        role,
        email: member.email,
        displayName: member.displayName ?? null,
        addedAt: Timestamp.now(),
      },
      updatedAt: serverTimestamp(),
    });
    return { ok: true };
  } catch {
    return { ok: false, reason: 'denied' };
  }
}

/**
 * Record on the account which lab it belongs to.
 *
 * Local storage already remembers the code for this device; this is what makes
 * it follow the person to their next one, and what `listLabMates` reads to work
 * out who your colleagues are.
 */
export async function joinLab(
  _uid: string,
  lab: { code: string; name: string },
): Promise<void> {
  // Membership decides what someone sees and what they are billed against, so
  // the server writes it — an account can no longer put itself in a lab.
  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('../lib/firebase');
  await httpsCallable<{ code: string }, { ok: true }>(functions, 'joinOrganisation')({ code: lab.code });
}

export async function setMemberRole(


  projectId: string,
  uid: string,
  role: Exclude<ProjectRole, 'owner'>,
) {
  await updateDoc(projectDoc(projectId), {
    [`members.${uid}.role`]: role,
    updatedAt: serverTimestamp(),
  });
}

export async function removeMember(projectId: string, uid: string) {
  await updateDoc(projectDoc(projectId), {
    memberUids: arrayRemove(uid),
    [`members.${uid}`]: deleteField(),
    updatedAt: serverTimestamp(),
  });
}

/** A shared member removing themselves. Same write, different intent. */
export async function leaveProject(projectId: string, uid: string) {
  await removeMember(projectId, uid);
}

export function roleOf(project: Project | null, uid: string | undefined): ProjectRole | null {
  if (!project || !uid) return null;
  if (project.ownerUid === uid) return 'owner';
  return project.members?.[uid]?.role ?? null;
}

export function canEdit(project: Project | null, uid: string | undefined): boolean {
  const role = roleOf(project, uid);
  return role === 'owner' || role === 'editor';
}
